import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} */
  #inFlightProductGrids = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  #isOpening = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    if (this.classList.contains('judy-product-card__quick-add')) {
      this.addEventListener('pointerdown', this.#prefetchProductPage, { passive: true });
      this.addEventListener('pointerenter', this.#prefetchProductPage, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    if (this.classList.contains('judy-product-card__quick-add')) {
      this.removeEventListener('pointerdown', this.#prefetchProductPage);
      this.removeEventListener('pointerenter', this.#prefetchProductPage);
    }
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  #prefetchProductPage = () => {
    const currentUrl = this.productPageUrl;
    if (!currentUrl || this.#cachedContent.has(currentUrl)) return;
    this.#ensureProductGrid(currentUrl);
  };

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  async #ensureProductGrid(productPageUrl) {
    if (!productPageUrl) return null;

    const cached = this.#cachedContent.get(productPageUrl);
    if (cached) return cached;

    const inFlight = this.#inFlightProductGrids.get(productPageUrl);
    if (inFlight) return inFlight;

    const promise = this.#fetchAndCacheProductGrid(productPageUrl);
    this.#inFlightProductGrids.set(productPageUrl, promise);

    try {
      return await promise;
    } finally {
      this.#inFlightProductGrids.delete(productPageUrl);
    }
  }

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  async #fetchAndCacheProductGrid(productPageUrl) {
    const html = await this.fetchProductPage(productPageUrl);
    if (!html) return null;

    const gridElement = html.querySelector('[data-product-grid-content]');
    if (!gridElement) return null;

    const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
    this.#cachedContent.set(productPageUrl, productGrid);
    return productGrid;
  }

  /**
   * @param {boolean} loading
   */
  #setModalLoading(loading) {
    const modal = document.querySelector('#quick-add-dialog .quick-add-modal');
    if (!(modal instanceof HTMLElement)) return;

    modal.classList.toggle('quick-add-modal--loading', loading);
    modal.toggleAttribute('aria-busy', loading);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const currentUrl = this.productPageUrl;
    if (!currentUrl || this.#isOpening) return;

    this.#isOpening = true;
    this.#openQuickAddModal();
    this.#setModalLoading(true);

    try {
      const productGrid = await this.#ensureProductGrid(currentUrl);

      if (productGrid) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
        this.#syncJudyModalClass(freshContent);
      }
    } finally {
      this.#setModalLoading(false);
      this.#isOpening = false;
    }
  };

  /**
   * @param {Element} productGrid
   */
  #syncJudyModalClass(productGrid) {
    const dialog = document.querySelector('#quick-add-dialog .quick-add-modal');
    if (!(dialog instanceof HTMLElement)) return;

    const isJudy = Boolean(productGrid.querySelector('.product-details--judy'));
    dialog.classList.toggle('quick-add-modal--judy', isJudy);
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    const modalContent = dialogComponent.querySelector('.quick-add-modal__content');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
    modalContent?.scrollTo({ top: 0, behavior: 'instant' });
    productDetails?.classList.remove('judy-scrollbar-active');
    modalContent?.classList.remove('judy-scrollbar-active');
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const isJudyQuickAdd = Boolean(productGrid.querySelector('.product-details--judy'));

    // Keep Judy modal DOM intact on mobile, because its layout CSS depends on
    // .product-details / .product-details--judy staying inside modal content.
    if (isMobileBreakpoint() && !isJudyQuickAdd) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#refreshVariantOptionScrollers(modalContent);
    this.#refreshModalMediaGallery(modalContent);
  }

  /**
   * After morph: reset carousel and eager-load gallery images (avoids blank slides in dialog).
   * @param {Element} root
   */
  #refreshModalMediaGallery(root) {
    const mediaRoot = root.querySelector('.product-information__media');
    if (!mediaRoot) return;

    mediaRoot.querySelectorAll('img').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      img.loading = 'eager';
      if (img.complete) return;
      const src = img.currentSrc || img.src;
      if (!src) return;
      const preload = new Image();
      preload.src = src;
    });

    const slideshow = mediaRoot.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement) || !('select' in slideshow)) return;

    const runSelect = () => {
      if (typeof slideshow.select === 'function') {
        slideshow.select(0, undefined, { animate: false });
      }
    };

    requestAnimationFrame(() => {
      runSelect();
      requestAnimationFrame(runSelect);
    });
  }

  /**
   * @param {Element} root
   */
  #refreshVariantOptionScrollers(root) {
    const scrollers = root.querySelectorAll('variant-option-scroller');
    scrollers.forEach((scroller) => {
      if ('refresh' in scroller && typeof scroller.refresh === 'function') {
        scroller.refresh();
      }
    });
    requestAnimationFrame(() => {
      scrollers.forEach((scroller) => {
        if ('refresh' in scroller && typeof scroller.refresh === 'function') {
          scroller.refresh();
        }
      });
    });
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

/** @type {WeakMap<HTMLElement, ReturnType<typeof setTimeout>>} */
const judyScrollbarHideTimers = new WeakMap();

/**
 * @param {HTMLElement} target
 */
function revealJudyScrollbar(target) {
  target.classList.add('judy-scrollbar-active');
  const existing = judyScrollbarHideTimers.get(target);
  if (existing) clearTimeout(existing);
  judyScrollbarHideTimers.set(
    target,
    setTimeout(() => {
      target.classList.remove('judy-scrollbar-active');
      judyScrollbarHideTimers.delete(target);
    }, 800)
  );
}

/**
 * @param {Event} event
 */
function handleJudyModalScroll(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (!target.matches('.product-details, .quick-add-modal__content')) return;
  const modal = target.closest('.quick-add-modal');
  if (!modal?.classList.contains('quick-add-modal--judy') && !modal?.querySelector('.product-details--judy')) return;
  revealJudyScrollbar(target);

  // Progressive media shrink on mobile while user scrolls details.
  // 0 => no shrink, 1 => fully compact.
  const shrinkDistance = 110;
  const progress = Math.max(0, Math.min(1, target.scrollTop / shrinkDistance));
  modal.style.setProperty('--judy-media-progress', progress.toFixed(3));
}

let judyModalScrollbarBound = false;

function bindJudyModalOverlayScrollbars() {
  if (judyModalScrollbarBound) return;
  const dialog = document.getElementById('quick-add-dialog');
  if (!dialog) return;
  judyModalScrollbarBound = true;
  dialog.addEventListener('scroll', handleJudyModalScroll, { capture: true, passive: true });
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    bindJudyModalOverlayScrollbars();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    this.querySelectorAll('.judy-scrollbar-active').forEach((el) => {
      el.classList.remove('judy-scrollbar-active');
    });
    const modal = this.querySelector('.quick-add-modal');
    modal?.style.removeProperty('--judy-media-progress');

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
